import React, { useState, useEffect } from 'react';
import { Paper, Typography, Box, CircularProgress } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import { useBookingDetails } from '../../../contexts/BookingDetailsContext';
import { formatActivityDateTime, getInitials, bookingTokens, cardSx, cardHeadSx, emptyStateSx } from '../bms_helper';

// Activity type icons mapping
const getActivityIcon = (type) => {
  switch (type) {
    case 'booking_created': return 'add_circle';
    case 'payment_added': return 'payments';
    case 'document_uploaded': return 'upload_file';
    case 'status_changed': return 'sync';
    default: return 'event';
  }
};

const ActivityItem = ({ activity, isLast }) => {
  const { date, time } = formatActivityDateTime(activity.timestamp);
  const initials = getInitials(activity.agent?.name) || '?';

  return (
    <Box sx={{ display: 'flex', gap: 0, position: 'relative' }}>
      <Box sx={{ width: 110, flexShrink: 0, pt: 0.25 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: bookingTokens.ink2 }}>{date}</Typography>
        <Typography sx={{ fontSize: 11, color: bookingTokens.muted, mt: 0.25 }}>{time}</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 40, flexShrink: 0 }}>
        <Box sx={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, zIndex: 1, background: `linear-gradient(135deg,${bookingTokens.green},#12a85e)`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, border: `3px solid ${bookingTokens.bg}` }}>{initials}</Box>
        {!isLast && <Box sx={{ width: 2, flex: 1, bgcolor: bookingTokens.borderL, minHeight: 20, mt: 0.25 }} />}
      </Box>
      <Box sx={{ flex: 1, pl: 1.75, pb: isLast ? 0 : 3 }}>
        <Box sx={{ bgcolor: bookingTokens.bg, border: `1px solid ${bookingTokens.border}`, borderRadius: 1.5, p: '12px 16px' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: bookingTokens.ink, mb: 0.5 }}>{activity.description}</Typography>
          <Typography sx={{ fontSize: 11.5, color: bookingTokens.muted, mb: 1 }}>By {activity.agent?.name || 'System'}</Typography>
          <Box component="span" sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: bookingTokens.green, background: `${bookingTokens.green}15`, border: `1px solid ${bookingTokens.green}40`, px: 1.125, py: 0.25, borderRadius: 0.75 }}>{activity.activity_type?.replaceAll('_', ' ') || 'Activity'}</Box>
        </Box>
      </Box>
    </Box>
  );
};

const BookingActivityTab = ({ booking }) => {
  const { fetchBookingActivities } = useBookingDetails();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const bookingId = booking?.id;

  useEffect(() => {
    const fetchActivities = async () => {
      if (!bookingId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const data = await fetchBookingActivities(bookingId);
        setActivities(data || []);
      } catch (err) {
        setError(err.message || 'Failed to load activities');
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [bookingId]); // ONLY trigger when bookingId changes

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper elevation={1} sx={{ p: 3, borderRadius: 3 }}>
        <Typography color="error">Error: {error}</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ ...cardSx, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={cardHeadSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component="span" sx={{ fontSize: 14 }}>📅</Typography>
          <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: bookingTokens.ink }}>Activity Timeline</Typography>
        </Box>
      </Box>
      <Box sx={{ p: '20px 24px', flex: 1, overflow: 'auto' }}>
        {activities.length > 0 ? (
          activities.map((activity, index) => (
            <ActivityItem key={activity.id} activity={activity} isLast={index === activities.length - 1} />
          ))
        ) : (
          <Box sx={emptyStateSx}>
            <Typography sx={{ fontSize: 40, opacity: 0.4 }}>🕐</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: bookingTokens.muted }}>No activities recorded yet.</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default BookingActivityTab;
